import * as Sentry from '@sentry/react';

const SENTRY_DSN = 'https://ad0c79e96701b1b21e86c0991163c556@o4510794324246528.ingest.de.sentry.io/4510794330734672';

export const initSentry = () => {
  // Solo inicializar en producción o si está habilitado en desarrollo
  if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEBUG) {
    console.log('Sentry disabled in development mode');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,

    // Enviar PII por defecto (IP, etc.) para diagnóstico
    sendDefaultPii: true,

    // Capturar el 100% de errores
    sampleRate: 1.0,

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    // Replay para ver sesiones con errores
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,

    // Distributed tracing - propagar traces a la API
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api\.jaradpetfood\.com/,
      /^https:\/\/jaradpetfood\.com/,
      /^https:\/\/.*\.beeapp\.es/,
    ],

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Filtrar errores no útiles
    ignoreErrors: [
      // Errores de extensiones de navegador
      'chrome-extension://',
      'moz-extension://',
      // Errores de ResizeObserver (comunes y no críticos)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Errores de navegación cancelada
      'AbortError',
    ],

    // NO ignoramos "Failed to fetch" porque queremos trackear estos errores

    // Antes de enviar, añadir contexto adicional
    beforeSend(event) {
      // Añadir información del usuario si está autenticado
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          event.user = {
            id: user.id,
            email: user.email,
            username: user.name,
          };
        } catch (e) {
          // Ignorar errores de parsing
        }
      }

      // Añadir contexto de navegador adicional
      event.tags = {
        ...event.tags,
        'browser.online': navigator.onLine ? 'true' : 'false',
        'browser.language': navigator.language,
      };

      return event;
    },
  });

  console.log('Sentry initialized');
};

// Función helper para capturar errores manualmente
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(error);
  });
};

// Función helper para capturar mensajes
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, unknown>) => {
  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureMessage(message, level);
  });
};

// Función para añadir breadcrumb personalizado
export const addBreadcrumb = (message: string, category: string, data?: Record<string, unknown>) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

export { Sentry };
